/**
 * Node customizado para verificação de gatilho
 * Permite ramificar o fluxo baseado no trigger que o iniciou
 * 
 * RELEASE: 1.0.0 - 2024-12-25 - Novo componente
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Target, Settings2 } from 'lucide-react';
import { FlowNodeData, TriggerType } from '@/types/flows';
import { useMemo } from 'react';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Labels amigáveis para tipos de trigger
 * 
 * RELEASE: 1.1.0 - 2024-12-26 - Adicionado 'any_message'
 * RELEASE: 1.2.0 - 2026-01-25 - Adicionado 'crm_filter' (filtro avançado CRM)
 */
const triggerLabels: Record<TriggerType, string> = {
  keyword: 'Palavra-chave',
  first_message: 'Primeira msg',
  scheduled: 'Agendado',
  inactivity: 'Inatividade',
  event: 'Evento',
  webhook: 'Webhook',
  contact_tag: 'Tag',
  phase: 'Fase',
  department: 'Depto',
  active_conversation: 'Conv. ativa',
  any_message: 'Qualquer msg',
  crm_filter: 'Filtro CRM',
  contract_event: 'Evento Contrato',
  campaign_action: 'Campanha',
  // RELEASE: 1.4.0 - 2026-08-30 - Removido 'workflow_stage_entered' (Workflow v1 descontinuado)

};

/**
 * Componente visual do node de verificação de gatilho
 */
export function TriggerCheckNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config || {};
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Lista de triggers configurados para verificar
  const triggersToCheck: TriggerType[] = config.triggers_to_check || [];
  
  // Obtém informações das edges conectadas via data injetado
  const connectedHandles = useMemo(() => {
    const edges = (data as any).edges || [];
    return new Set(edges.map((e: any) => e.sourceHandle));
  }, [data]);

  return (
    <div className={`bg-card rounded-lg shadow-lg border-2 min-w-[220px] transition-all ${selected ? 'border-amber-500 ring-2 ring-amber-500/30' : 'border-amber-400'}`}>
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      {/* Handle de entrada no topo */}
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-amber-500 !w-3 !h-3"
      />
      
      {/* Cabeçalho do node */}
      <div className="bg-amber-500/10 px-3 py-2 rounded-t-lg border-b border-amber-200 dark:border-amber-800 flex items-center gap-2">
        <div className="bg-amber-500/20 text-amber-600 p-1.5 rounded">
          <Target className="h-4 w-4" />
        </div>
        <span className="font-semibold text-sm text-foreground">{nodeData.config?.nickname || 'Verificar Gatilho'}</span>
      </div>
      
      {/* Corpo com lista de triggers */}
      <div className="p-2 space-y-2">
        {/* Lista de triggers configurados */}
        {triggersToCheck.map((trigger, index) => {
          const isConnected = connectedHandles.has(trigger);
          
          return (
            <div key={trigger} className="relative">
              <div className="text-xs px-2 py-1.5 rounded border bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-amber-700 dark:text-amber-400">
                    {triggerLabels[trigger] || trigger}
                  </span>
                </div>
              </div>
              
              {/* Handle de saída para este trigger */}
              <Handle
                type="source"
                position={Position.Right}
                id={trigger}
                className={`!w-3 !h-3 transition-all ${
                  isConnected 
                    ? '!bg-amber-500' 
                    : '!bg-red-500 animate-pulse'
                }`}
                style={{ top: '50%', transform: 'translateY(-50%)' }}
              />
            </div>
          );
        })}
        
        {/* Bloco Default (sempre presente) */}
        <div className="relative">
          <div className="text-xs px-2 py-1.5 rounded border bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700">
            <span className="font-medium text-gray-600 dark:text-gray-400">Padrão</span>
          </div>
          
          {/* Handle de saída para Default */}
          <Handle
            type="source"
            position={Position.Right}
            id="default"
            className={`!w-3 !h-3 transition-all ${
              connectedHandles.has('default') 
                ? '!bg-gray-500' 
                : '!bg-red-500 animate-pulse'
            }`}
            style={{ top: '50%', transform: 'translateY(-50%)' }}
          />
        </div>
        
        {/* Indicador de configuração vazia */}
        {triggersToCheck.length === 0 && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground px-2">
            <Settings2 className="h-3 w-3" />
            <span>Clique para configurar</span>
          </div>
        )}
      </div>
    </div>
  );
}
